import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Shield, Users, FileText, Tag, BarChart3, Plus, Edit, Trash2, 
  Search, ChevronDown, Eye, Crown, AlertTriangle, Settings,
  Calendar, TrendingUp, BookOpen, X, Save, Image, Video, LogOut, Key
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, isAuthenticated, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [discountCodes, setDiscountCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showPostModal, setShowPostModal] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [postForm, setPostForm] = useState({
    title: '', content: '', category: 'update', excerpt: '',
    image_url: '', video_url: '', published: true
  });
  const [discountForm, setDiscountForm] = useState({
    code: '', discount_percent: 10, max_uses: 100, 
    valid_until: '', tier: 'all', active: true
  });
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    // Wait for auth to finish loading before checking
    if (authLoading) return;
    
    if (!isAuthenticated) {
      navigate('/');
      return;
    }
    if (user && !user.is_admin) {
      navigate('/');
      return;
    }
    if (user && user.is_admin) {
      fetchDashboardData();
    }
  }, [isAuthenticated, user, authLoading]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, postsRes, discountsRes] = await Promise.allSettled([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/users`, { headers }),
        axios.get(`${API}/blog/posts`),
        axios.get(`${API}/admin/discount-codes`, { headers })
      ]);
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
      if (usersRes.status === 'fulfilled') setUsers(usersRes.value.data.users || []);
      if (postsRes.status === 'fulfilled') setPosts(postsRes.value.data.posts || []);
      if (discountsRes.status === 'fulfilled') setDiscountCodes(discountsRes.value.data.codes || []);
    } catch (err) {
      console.error('Error fetching admin data:', err);
    }
    setLoading(false);
  };

  const handleCreatePost = async () => {
    try {
      if (editingPost) {
        await axios.put(`${API}/admin/blog/posts/${editingPost}`, postForm, { headers });
      } else {
        await axios.post(`${API}/admin/blog/posts`, postForm, { headers });
      }
      setShowPostModal(false);
      setEditingPost(null);
      setPostForm({ title: '', content: '', category: 'update', excerpt: '', image_url: '', video_url: '', published: true });
      fetchDashboardData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save post');
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await axios.delete(`${API}/admin/blog/posts/${postId}`, { headers });
      fetchDashboardData();
    } catch (err) {
      alert('Failed to delete post');
    }
  };

  const handleEditPost = (post) => {
    setEditingPost(post.id);
    setPostForm({
      title: post.title, content: post.content, category: post.category,
      excerpt: post.excerpt || '', image_url: post.image_url || '',
      video_url: post.video_url || '', published: post.published
    });
    setShowPostModal(true);
  };

  const handleCreateDiscount = async () => {
    try {
      if (editingDiscount) {
        await axios.put(`${API}/admin/discount-codes/${editingDiscount}`, discountForm, { headers });
      } else {
        await axios.post(`${API}/admin/discount-codes`, discountForm, { headers });
      }
      setShowDiscountModal(false);
      setEditingDiscount(null);
      setDiscountForm({ code: '', discount_percent: 10, max_uses: 100, valid_until: '', tier: 'all', active: true });
      fetchDashboardData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save discount code');
    }
  };

  const handleDeleteDiscount = async (codeId) => {
    if (!window.confirm('Delete this discount code?')) return;
    try {
      await axios.delete(`${API}/admin/discount-codes/${codeId}`, { headers });
      fetchDashboardData();
    } catch (err) {
      alert('Failed to delete discount code');
    }
  };

  const handleEditDiscount = (code) => {
    setEditingDiscount(code.id);
    setDiscountForm({
      code: code.code, discount_percent: code.discount_percent,
      max_uses: code.max_uses, valid_until: code.valid_until || '',
      tier: code.tier || 'all', active: code.active
    });
    setShowDiscountModal(true);
  };

  const handleToggleAdmin = async (userId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role`, { is_admin: !currentStatus }, { headers });
      fetchDashboardData();
    } catch (err) {
      alert('Failed to update user role');
    }
  };

  const handleOpenResetPassword = (user) => {
    setResetPasswordUser(user);
    setNewPassword('');
    setShowResetPasswordModal(true);
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }
    
    setResetPasswordLoading(true);
    try {
      await axios.post(`${API}/admin/users/${resetPasswordUser.id}/reset-password`, 
        { new_password: newPassword }, 
        { headers }
      );
      alert(`Password reset successfully for ${resetPasswordUser.email}`);
      setShowResetPasswordModal(false);
      setResetPasswordUser(null);
      setNewPassword('');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to reset password');
    }
    setResetPasswordLoading(false);
  };

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Show loading while auth is being checked
  if (authLoading) {
    return (
      <div className="admin-loading" data-testid="admin-loading">
        <div className="spinner" />
        <p>Checking authentication...</p>
      </div>
    );
  }

  if (!isAuthenticated || !user?.is_admin) {
    return (
      <div className="admin-access-denied" data-testid="admin-access-denied">
        <AlertTriangle size={48} />
        <h2>Access Denied</h2>
        <p>You need admin privileges to access this page.</p>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: <BarChart3 size={18} /> },
    { id: 'users', label: 'Users', icon: <Users size={18} /> },
    { id: 'blog', label: 'Blog Posts', icon: <FileText size={18} /> },
    { id: 'discounts', label: 'Discount Codes', icon: <Tag size={18} /> },
  ];

  return (
    <div className="admin-dashboard" data-testid="admin-dashboard">
      <div className="admin-header">
        <div className="admin-header-content">
          <Shield size={28} />
          <div>
            <h1>Admin Dashboard</h1>
            <p>Manage your platform</p>
          </div>
        </div>
      </div>

      <div className="admin-layout">
        <aside className="admin-sidebar" data-testid="admin-sidebar">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`admin-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`admin-tab-${tab.id}`}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          ))}
        </aside>

        <main className="admin-main">
          {loading ? (
            <div className="admin-loading">
              <div className="spinner" />
              <p>Loading dashboard...</p>
            </div>
          ) : (
            <>
              {activeTab === 'overview' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="admin-section" data-testid="admin-overview">
                  <h2>Platform Overview</h2>
                  <div className="stats-grid">
                    <div className="stat-card" data-testid="stat-total-users">
                      <Users size={24} />
                      <div className="stat-info">
                        <span className="stat-value">{stats?.total_users || 0}</span>
                        <span className="stat-label">Total Users</span>
                      </div>
                    </div>
                    <div className="stat-card" data-testid="stat-pro-subscribers">
                      <Crown size={24} />
                      <div className="stat-info">
                        <span className="stat-value">{stats?.pro_subscribers || 0}</span>
                        <span className="stat-label">Pro Subscribers</span>
                      </div>
                    </div>
                    <div className="stat-card" data-testid="stat-premium-subscribers">
                      <TrendingUp size={24} />
                      <div className="stat-info">
                        <span className="stat-value">{stats?.premium_subscribers || 0}</span>
                        <span className="stat-label">Premium Subscribers</span>
                      </div>
                    </div>
                    <div className="stat-card" data-testid="stat-total-posts">
                      <FileText size={24} />
                      <div className="stat-info">
                        <span className="stat-value">{stats?.total_posts || 0}</span>
                        <span className="stat-label">Blog Posts</span>
                      </div>
                    </div>
                    <div className="stat-card" data-testid="stat-contact-submissions">
                      <BookOpen size={24} />
                      <div className="stat-info">
                        <span className="stat-value">{stats?.contact_submissions || 0}</span>
                        <span className="stat-label">Contact Submissions</span>
                      </div>
                    </div>
                    <div className="stat-card" data-testid="stat-active-discounts">
                      <Tag size={24} />
                      <div className="stat-info">
                        <span className="stat-value">{stats?.active_discounts || 0}</span>
                        <span className="stat-label">Active Discounts</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {activeTab === 'users' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="admin-section" data-testid="admin-users">
                  <div className="section-header">
                    <h2>User Management</h2>
                    <div className="search-bar">
                      <Search size={18} />
                      <input 
                        type="text" placeholder="Search users..." 
                        value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                        data-testid="user-search-input"
                      />
                    </div>
                  </div>
                  <div className="admin-table-wrapper">
                    <table className="admin-table" data-testid="users-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Tier</th>
                          <th>Verified</th>
                          <th>Admin</th>
                          <th>Joined</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUsers.map(u => (
                          <tr key={u.id} data-testid={`user-row-${u.id}`}>
                            <td>{u.name}</td>
                            <td>{u.email}</td>
                            <td><span className={`tier-badge tier-${u.subscription_tier}`}>{u.subscription_tier}</span></td>
                            <td>{u.is_email_verified ? 'Yes' : 'No'}</td>
                            <td>{u.is_admin ? 'Yes' : 'No'}</td>
                            <td>{new Date(u.created_at).toLocaleDateString()}</td>
                            <td className="action-cell">
                              <button 
                                className="admin-action-btn"
                                onClick={() => handleToggleAdmin(u.id, u.is_admin)}
                                data-testid={`toggle-admin-${u.id}`}
                                title={u.is_admin ? 'Remove Admin' : 'Make Admin'}
                              >
                                <Shield size={14} />
                              </button>
                              <button 
                                className="admin-action-btn warning"
                                onClick={() => handleOpenResetPassword(u)}
                                data-testid={`reset-password-${u.id}`}
                                title="Reset Password"
                              >
                                <Key size={14} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {filteredUsers.length === 0 && <p className="no-data">No users found</p>}
                  </div>
                </motion.div>
              )}

              {activeTab === 'blog' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="admin-section" data-testid="admin-blog">
                  <div className="section-header">
                    <h2>Blog Posts</h2>
                    <button className="btn btn-primary" onClick={() => { setEditingPost(null); setPostForm({ title: '', content: '', category: 'update', excerpt: '', image_url: '', video_url: '', published: true }); setShowPostModal(true); }} data-testid="create-post-btn">
                      <Plus size={18} /> New Post
                    </button>
                  </div>
                  <div className="admin-table-wrapper">
                    <table className="admin-table" data-testid="posts-table">
                      <thead>
                        <tr>
                          <th>Title</th>
                          <th>Category</th>
                          <th>Status</th>
                          <th>Date</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {posts.map(post => (
                          <tr key={post.id} data-testid={`post-row-${post.id}`}>
                            <td>{post.title}</td>
                            <td><span className="category-badge">{post.category}</span></td>
                            <td><span className={`status-badge ${post.published ? 'published' : 'draft'}`}>{post.published ? 'Published' : 'Draft'}</span></td>
                            <td>{new Date(post.created_at).toLocaleDateString()}</td>
                            <td className="action-cell">
                              <button className="admin-action-btn" onClick={() => handleEditPost(post)} data-testid={`edit-post-${post.id}`}><Edit size={14} /></button>
                              <button className="admin-action-btn danger" onClick={() => handleDeletePost(post.id)} data-testid={`delete-post-${post.id}`}><Trash2 size={14} /></button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {posts.length === 0 && <p className="no-data">No blog posts yet. Create your first post!</p>}
                  </div>
                </motion.div>
              )}

              {activeTab === 'discounts' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="admin-section" data-testid="admin-discounts">
                  <div className="section-header">
                    <h2>Discount Codes</h2>
                    <button className="btn btn-primary" onClick={() => { setEditingDiscount(null); setDiscountForm({ code: '', discount_percent: 10, max_uses: 100, valid_until: '', tier: 'all', active: true }); setShowDiscountModal(true); }} data-testid="create-discount-btn">
                      <Plus size={18} /> New Code
                    </button>
                  </div>
                  <div className="admin-table-wrapper">
                    <table className="admin-table" data-testid="discounts-table">
                      <thead>
                        <tr>
                          <th>Code</th>
                          <th>Discount</th>
                          <th>Uses</th>
                          <th>Tier</th>
                          <th>Status</th>
                          <th>Expires</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {discountCodes.map(code => (
                          <tr key={code.id} data-testid={`discount-row-${code.id}`}>
                            <td><code>{code.code}</code></td>
                            <td>{code.discount_percent}%</td>
                            <td>{code.times_used || 0}/{code.max_uses}</td>
                            <td>{code.tier}</td>
                            <td><span className={`status-badge ${code.active ? 'published' : 'draft'}`}>{code.active ? 'Active' : 'Inactive'}</span></td>
                            <td>{code.valid_until ? new Date(code.valid_until).toLocaleDateString() : 'No expiry'}</td>
                            <td className="action-cell">
                              <button className="admin-action-btn" onClick={() => handleEditDiscount(code)} data-testid={`edit-discount-${code.id}`}><Edit size={14} /></button>
                              <button className="admin-action-btn danger" onClick={() => handleDeleteDiscount(code.id)} data-testid={`delete-discount-${code.id}`}><Trash2 size={14} /></button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {discountCodes.length === 0 && <p className="no-data">No discount codes yet. Create one!</p>}
                  </div>
                </motion.div>
              )}
            </>
          )}
        </main>
      </div>

      {/* Post Modal */}
      {showPostModal && (
        <div className="admin-modal-overlay" data-testid="post-modal">
          <div className="admin-modal">
            <div className="modal-header">
              <h3>{editingPost ? 'Edit Post' : 'Create New Post'}</h3>
              <button onClick={() => setShowPostModal(false)} className="modal-close"><X size={20} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Title</label>
                <input type="text" value={postForm.title} onChange={e => setPostForm({...postForm, title: e.target.value})} placeholder="Post title..." data-testid="post-title-input" />
              </div>
              <div className="form-group">
                <label>Category</label>
                <select value={postForm.category} onChange={e => setPostForm({...postForm, category: e.target.value})} data-testid="post-category-select">
                  <option value="update">Update</option>
                  <option value="lesson">Lesson Learned</option>
                  <option value="milestone">Milestone</option>
                  <option value="review">Review</option>
                  <option value="announcement">Announcement</option>
                </select>
              </div>
              <div className="form-group">
                <label>Excerpt</label>
                <input type="text" value={postForm.excerpt} onChange={e => setPostForm({...postForm, excerpt: e.target.value})} placeholder="Brief summary..." data-testid="post-excerpt-input" />
              </div>
              <div className="form-group">
                <label>Content</label>
                <textarea rows={10} value={postForm.content} onChange={e => setPostForm({...postForm, content: e.target.value})} placeholder="Write your post..." data-testid="post-content-input" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label><Image size={14} /> Image URL</label>
                  <input type="text" value={postForm.image_url} onChange={e => setPostForm({...postForm, image_url: e.target.value})} placeholder="https://..." data-testid="post-image-input" />
                </div>
                <div className="form-group">
                  <label><Video size={14} /> Video URL</label>
                  <input type="text" value={postForm.video_url} onChange={e => setPostForm({...postForm, video_url: e.target.value})} placeholder="https://..." data-testid="post-video-input" />
                </div>
              </div>
              <div className="form-group checkbox-group">
                <label>
                  <input type="checkbox" checked={postForm.published} onChange={e => setPostForm({...postForm, published: e.target.checked})} data-testid="post-published-checkbox" />
                  Published
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowPostModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCreatePost} data-testid="save-post-btn">
                <Save size={16} /> {editingPost ? 'Update' : 'Create'} Post
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Discount Modal */}
      {showDiscountModal && (
        <div className="admin-modal-overlay" data-testid="discount-modal">
          <div className="admin-modal">
            <div className="modal-header">
              <h3>{editingDiscount ? 'Edit Discount Code' : 'Create Discount Code'}</h3>
              <button onClick={() => setShowDiscountModal(false)} className="modal-close"><X size={20} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Code</label>
                <input type="text" value={discountForm.code} onChange={e => setDiscountForm({...discountForm, code: e.target.value.toUpperCase()})} placeholder="e.g. WELCOME20" data-testid="discount-code-input" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Discount %</label>
                  <input type="number" min={1} max={100} value={discountForm.discount_percent} onChange={e => setDiscountForm({...discountForm, discount_percent: parseInt(e.target.value) || 0})} data-testid="discount-percent-input" />
                </div>
                <div className="form-group">
                  <label>Max Uses</label>
                  <input type="number" min={1} value={discountForm.max_uses} onChange={e => setDiscountForm({...discountForm, max_uses: parseInt(e.target.value) || 1})} data-testid="discount-max-uses-input" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Applicable Tier</label>
                  <select value={discountForm.tier} onChange={e => setDiscountForm({...discountForm, tier: e.target.value})} data-testid="discount-tier-select">
                    <option value="all">All Tiers</option>
                    <option value="pro">Pro Only</option>
                    <option value="premium">Premium Only</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Valid Until</label>
                  <input type="date" value={discountForm.valid_until} onChange={e => setDiscountForm({...discountForm, valid_until: e.target.value})} data-testid="discount-valid-until-input" />
                </div>
              </div>
              <div className="form-group checkbox-group">
                <label>
                  <input type="checkbox" checked={discountForm.active} onChange={e => setDiscountForm({...discountForm, active: e.target.checked})} data-testid="discount-active-checkbox" />
                  Active
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowDiscountModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCreateDiscount} data-testid="save-discount-btn">
                <Save size={16} /> {editingDiscount ? 'Update' : 'Create'} Code
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && resetPasswordUser && (
        <div className="admin-modal-overlay" onClick={() => setShowResetPasswordModal(false)} data-testid="reset-password-modal">
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3><Key size={20} /> Reset Password</h3>
              <button className="modal-close" onClick={() => setShowResetPasswordModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="reset-password-info">
                <p><strong>User:</strong> {resetPasswordUser.name}</p>
                <p><strong>Email:</strong> {resetPasswordUser.email}</p>
              </div>
              <div className="form-group">
                <label>New Password</label>
                <input 
                  type="text" 
                  value={newPassword} 
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 6 characters)"
                  data-testid="new-password-input"
                />
              </div>
              <p className="password-note">
                <AlertTriangle size={14} /> The user will need to use this password to log in. Consider informing them of the change.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowResetPasswordModal(false)}>Cancel</button>
              <button 
                className="btn btn-warning" 
                onClick={handleResetPassword}
                disabled={resetPasswordLoading || !newPassword || newPassword.length < 6}
                data-testid="confirm-reset-password-btn"
              >
                <Key size={16} /> {resetPasswordLoading ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .admin-dashboard {
          min-height: 100vh;
          padding-bottom: 60px;
        }
        .admin-header {
          background: linear-gradient(135deg, rgba(0, 255, 148, 0.1), rgba(0, 212, 170, 0.05));
          border-bottom: 1px solid var(--border);
          padding: 32px 24px;
        }
        .admin-header-content {
          max-width: 1200px;
          margin: 0 auto;
          display: flex;
          align-items: center;
          gap: 16px;
          color: var(--primary);
        }
        .admin-header h1 {
          font-size: 1.5rem;
          margin: 0;
          color: var(--text-primary);
        }
        .admin-header p {
          margin: 0;
          color: var(--text-muted);
          font-size: 14px;
        }
        .admin-layout {
          max-width: 1200px;
          margin: 0 auto;
          display: flex;
          gap: 24px;
          padding: 24px;
        }
        .admin-sidebar {
          width: 200px;
          flex-shrink: 0;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .admin-tab {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 16px;
          background: none;
          border: 1px solid transparent;
          border-radius: 8px;
          color: var(--text-muted);
          cursor: pointer;
          font-size: 14px;
          transition: all 0.2s;
          text-align: left;
        }
        .admin-tab:hover { background: var(--bg-subtle); color: var(--text-primary); }
        .admin-tab.active { background: var(--bg-subtle); color: var(--primary); border-color: var(--primary); }
        .admin-main { flex: 1; min-width: 0; }
        .admin-section h2 { margin: 0 0 24px; font-size: 1.25rem; }
        .section-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; }
        .section-header h2 { margin: 0; }
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 16px;
        }
        .stat-card {
          background: var(--bg-paper);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 20px;
          display: flex;
          align-items: center;
          gap: 16px;
        }
        .stat-card svg { color: var(--primary); }
        .stat-info { display: flex; flex-direction: column; }
        .stat-value { font-size: 1.5rem; font-weight: 700; color: var(--text-primary); }
        .stat-label { font-size: 13px; color: var(--text-muted); }
        .search-bar {
          display: flex;
          align-items: center;
          gap: 8px;
          background: var(--bg-paper);
          border: 1px solid var(--border);
          border-radius: 8px;
          padding: 8px 14px;
        }
        .search-bar input {
          background: none;
          border: none;
          color: var(--text-primary);
          font-size: 14px;
          outline: none;
          width: 200px;
        }
        .search-bar svg { color: var(--text-muted); }
        .admin-table-wrapper { overflow-x: auto; }
        .admin-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 14px;
        }
        .admin-table th {
          text-align: left;
          padding: 12px 16px;
          color: var(--text-muted);
          font-weight: 600;
          border-bottom: 1px solid var(--border);
          white-space: nowrap;
        }
        .admin-table td {
          padding: 12px 16px;
          border-bottom: 1px solid var(--border);
          color: var(--text-secondary);
        }
        .admin-table tr:hover td { background: var(--bg-subtle); }
        .tier-badge {
          display: inline-block;
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
          text-transform: capitalize;
        }
        .tier-free { background: rgba(255,255,255,0.1); color: var(--text-muted); }
        .tier-pro { background: rgba(0,255,148,0.15); color: var(--primary); }
        .tier-premium { background: rgba(255,215,0,0.15); color: var(--gold); }
        .category-badge {
          display: inline-block;
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 12px;
          background: rgba(0,255,148,0.1);
          color: var(--primary);
          text-transform: capitalize;
        }
        .status-badge {
          display: inline-block;
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
        }
        .status-badge.published { background: rgba(0,255,148,0.15); color: var(--primary); }
        .status-badge.draft { background: rgba(255,204,0,0.15); color: var(--warning); }
        .action-cell { display: flex; gap: 8px; }
        .admin-action-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          background: var(--bg-subtle);
          border: 1px solid var(--border);
          border-radius: 6px;
          color: var(--text-secondary);
          cursor: pointer;
          font-size: 12px;
          transition: all 0.2s;
          white-space: nowrap;
        }
        .admin-action-btn:hover { border-color: var(--primary); color: var(--primary); }
        .admin-action-btn.danger:hover { border-color: var(--error); color: var(--error); }
        .no-data { text-align: center; color: var(--text-muted); padding: 40px; }
        .admin-loading { display: flex; flex-direction: column; align-items: center; gap: 16px; padding: 60px; }
        .spinner { width: 40px; height: 40px; border: 3px solid var(--border); border-top-color: var(--primary); border-radius: 50%; animation: spin 0.8s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .admin-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }
        .admin-modal {
          background: var(--bg-paper);
          border: 1px solid var(--border);
          border-radius: 16px;
          width: 100%;
          max-width: 640px;
          max-height: 90vh;
          overflow-y: auto;
        }
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid var(--border);
        }
        .modal-header h3 { margin: 0; font-size: 1.1rem; }
        .modal-close { background: none; border: none; color: var(--text-muted); cursor: pointer; padding: 4px; }
        .modal-close:hover { color: var(--text-primary); }
        .modal-body { padding: 24px; }
        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 16px 24px;
          border-top: 1px solid var(--border);
        }
        .form-group { margin-bottom: 16px; }
        .form-group label {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          font-weight: 600;
          color: var(--text-secondary);
          margin-bottom: 6px;
        }
        .form-group input, .form-group select, .form-group textarea {
          width: 100%;
          padding: 10px 14px;
          background: var(--bg-subtle);
          border: 1px solid var(--border);
          border-radius: 8px;
          color: var(--text-primary);
          font-size: 14px;
          font-family: inherit;
          outline: none;
          transition: border-color 0.2s;
        }
        .form-group input:focus, .form-group select:focus, .form-group textarea:focus { border-color: var(--primary); }
        .form-group textarea { resize: vertical; }
        .form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        .checkbox-group label { display: flex; align-items: center; gap: 8px; cursor: pointer; }
        .checkbox-group input[type="checkbox"] { width: auto; accent-color: var(--primary); }
        .admin-action-btn.warning:hover { border-color: var(--warning); color: var(--warning); }
        .btn-warning {
          background: var(--warning);
          color: #0a0a0f;
          border: none;
        }
        .btn-warning:hover { opacity: 0.9; }
        .btn-warning:disabled { opacity: 0.5; cursor: not-allowed; }
        .reset-password-info {
          background: var(--bg-subtle);
          padding: 16px;
          border-radius: 8px;
          margin-bottom: 16px;
        }
        .reset-password-info p {
          margin: 4px 0;
          color: var(--text-secondary);
        }
        .reset-password-info strong {
          color: var(--text-primary);
        }
        .password-note {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          color: var(--warning);
          margin-top: 12px;
        }
        .admin-access-denied {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 60vh;
          gap: 16px;
          color: var(--text-muted);
        }
        .admin-access-denied svg { color: var(--warning); }
        .admin-access-denied h2 { color: var(--text-primary); margin: 0; }
        @media (max-width: 768px) {
          .admin-layout { flex-direction: column; }
          .admin-sidebar { width: 100%; flex-direction: row; overflow-x: auto; }
          .admin-tab span { display: none; }
          .form-row { grid-template-columns: 1fr; }
          .stats-grid { grid-template-columns: 1fr 1fr; }
        }
      `}</style>
    </div>
  );
};

export default AdminDashboard;
